import React from 'react';

/**
 * Circular progress ring (SVG). Renders a track + a colored arc representing
 * `value / max`. Defaults match the macro tile sizing in the home design.
 *
 *   <Ring value={1840} max={2200} color="var(--primary)" size={48} stroke={5} />
 *
 * `label` overlays a centered text node (e.g. for showing percentage in the
 * middle of the ring). `value` is clamped to `max` so the arc never wraps.
 */
export default function Ring({
  value = 0,
  max = 100,
  color = 'var(--primary)',
  size = 56,
  stroke = 6,
  label,
  trackColor = 'var(--border-soft)',
  className,
}) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const safeMax = max || 1;
  const off = c - (Math.min(value, safeMax) / safeMax) * c;
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      aria-hidden={label ? undefined : true}
      role={label ? 'img' : undefined}
    >
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        stroke={trackColor}
        strokeWidth={stroke}
      />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        stroke={color}
        strokeWidth={stroke}
        strokeLinecap="round"
        strokeDasharray={c}
        strokeDashoffset={off}
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
      />
      {label ? (
        <text
          x="50%"
          y="54%"
          textAnchor="middle"
          fontSize={Math.max(10, Math.round(size * 0.2))}
          fontWeight="600"
          fill="var(--fg)"
        >
          {label}
        </text>
      ) : null}
    </svg>
  );
}
